/**
 * Severity Threshold Cell Renderer
 *
 * Displays severity threshold for notifications as a colored badge with tooltip.
 * Shows the minimum severity level that triggers notifications.
 * Tooltip shows the included severity levels (e.g., "Medium, High, Critical").
 *
 * Uses RiskTag component for consistent severity color coding.
 */
import type { TagSeverity } from '@/components/chariot-ui';
import { RiskTag } from '@/components/chariot-ui';
import React from 'react';

import { Tooltip } from '@/components/Tooltip';
import {
  SEVERITY_OPTIONS,
  SEVERITY_THRESHOLD_MAP,
} from '@/features/integrations/config/severity.config';
import type { SeverityLevel } from '@/features/integrations/types/configuration.types';

interface SeverityThresholdCellProps {
  /** Severity threshold code (C, HC, MHC, LMHC, ILMHC) */
  threshold: string;
  /** Optional data-testid */
  'data-testid'?: string;
}

/**
 * Map threshold codes to TagSeverity values for RiskTag coloring
 * Exported for testing and diagnostic purposes
 */
export const THRESHOLD_TO_SEVERITY: Record<string, TagSeverity> = {
  C: 'critical',
  HC: 'high',
  MHC: 'medium',
  LMHC: 'low',
  ILMHC: 'info',
};

/**
 * Map severity codes to human-readable labels for tooltip
 */
const SEVERITY_CODE_LABELS: Record<string, string> = {
  C: 'Critical',
  H: 'High',
  M: 'Medium',
  L: 'Low',
  I: 'Info',
};

/**
 * Get the included severities as human-readable text for tooltip
 */
function getIncludedSeveritiesText(threshold: string): string {
  const includedCodes = SEVERITY_THRESHOLD_MAP[threshold as SeverityLevel] || [
    'M',
    'H',
    'C',
  ];
  return includedCodes
    .map(code => SEVERITY_CODE_LABELS[code] || code)
    .join(', ');
}

/**
 * Get the display label for a threshold (e.g., 'MHC' -> 'Medium')
 */
function getThresholdLabel(threshold: string): string {
  const option = SEVERITY_OPTIONS.find(opt => opt.value === threshold);
  return option?.label || 'Medium';
}

export const SeverityThresholdCell: React.FC<SeverityThresholdCellProps> = ({
  threshold = 'MHC',
  'data-testid': testId,
}) => {
  const severity = THRESHOLD_TO_SEVERITY[threshold] || 'medium';
  const tooltipText = getIncludedSeveritiesText(threshold);

  return (
    <Tooltip title={tooltipText}>
      <div data-testid={testId}>
        <RiskTag severity={severity} size="xs" />
      </div>
    </Tooltip>
  );
};

SeverityThresholdCell.displayName = 'SeverityThresholdCell';
